import { useCallback, useEffect, useRef, useState } from "react";
import { portalFetch } from "../api/client";
import {
	DiscoveryRefreshCoordinator,
	discoveryEventsUrl,
	fetchDiscoverySnapshot,
	reportDiscoverySyncTelemetry,
	shouldApplyDiscoverySnapshot,
} from "../api/discovery";
import { DiscoveryLiveConnection, type DiscoverySyncState } from "../api/discoveryLive";
import type { PortalDiscoverySnapshotDto, PortalProjectContextDto, PortalProjectDto, PortalWorkMapDto } from "../model";
import { navigatePortal } from "../router";
import { ArtifactView } from "./ArtifactView";
import { DiscoveryWorkspace, type WayfinderPane } from "./DiscoveryWorkspace";
import { InboxView } from "./InboxView";
import { MessageBody } from "./MessageBody";
import { PortalSelect } from "./PortalSelect";
import { PreviewView } from "./PreviewView";
import { ProjectCompletion, ProjectDelivery, ProjectRequests } from "./ProjectOperations";
import { SpecProgress, SpecView } from "./SpecReadiness";
import { StatusBanner } from "./StatusBanner";
import { humanizePortalStatus, needsFundingReasonLabel } from "./statusLabels";
export function ProjectDetailView({
	projectId,
	section,
	pushPublicKey,
	previewAvailable,
	voiceAvailable,
	agentModel,
}: {
	projectId: string;
	section:
		| "overview"
		| "conversation"
		| "spec"
		| "delivery"
		| "completion"
		| "requests"
		| "artifacts"
		| "inbox"
		| "preview";
	pushPublicKey?: string;
	previewAvailable?: boolean;
	voiceAvailable?: boolean;
	agentModel: string;
}) {
	const [project, setProject] = useState<PortalProjectDto>();
	const [context, setContext] = useState<PortalProjectContextDto>();
	const [maps, setMaps] = useState<PortalWorkMapDto[]>([]);
	const [selectedMapId, setSelectedMapId] = useState(
		() => new URLSearchParams(window.location.search).get("mapId") ?? undefined,
	);
	const [mapsLoaded, setMapsLoaded] = useState(false);
	const [discoverySnapshot, setDiscoverySnapshot] = useState<PortalDiscoverySnapshotDto>();
	const discoverySnapshotRef = useRef<PortalDiscoverySnapshotDto | undefined>(undefined);
	const latestDiscoveryRevision = useRef(new Map<string, number>());
	const discoveryRefreshCoordinator = useRef<DiscoveryRefreshCoordinator | undefined>(undefined);
	const discoveryLiveConnection = useRef<DiscoveryLiveConnection | undefined>(undefined);
	const [discoverySyncState, setDiscoverySyncState] = useState<DiscoverySyncState>("reconnecting");
	const [activeDiscoveryPane, setActiveDiscoveryPane] = useState<WayfinderPane>("chat");
	const [correctionMessage, setCorrectionMessage] = useState<{ key: string; mapId: string; message: string }>();
	const selectedMapIdRef = useRef(selectedMapId);
	selectedMapIdRef.current = selectedMapId;
	const [error, setError] = useState("");
	const [notice, setNotice] = useState<{ message: string }>();
	useEffect(() => {
		if (!notice) return;
		const timeout = window.setTimeout(() => {
			setNotice((current) => (current === notice ? undefined : current));
		}, 10_000);
		return () => window.clearTimeout(timeout);
	}, [notice]);
	const [showNewMap, setShowNewMap] = useState(false);
	const [newMapTitle, setNewMapTitle] = useState("");
	const [newMapDestination, setNewMapDestination] = useState("");
	const [creatingMap, setCreatingMap] = useState(false);
	const subnavRef = useRef<HTMLElement>(null);
	const mobileProjectMenuRef = useRef<HTMLDetailsElement>(null);
	const loadContext = useCallback(async () => {
		if (!selectedMapId) return setContext(undefined);
		setContext(
			await portalFetch<PortalProjectContextDto>(
				`/api/portal/v1/projects/${projectId}/context?mapId=${encodeURIComponent(selectedMapId)}`,
			),
		);
	}, [projectId, selectedMapId]);
	const selectMap = useCallback(
		async (mapId: string) => {
			try {
				const nextContext = await portalFetch<PortalProjectContextDto>(
					`/api/portal/v1/projects/${projectId}/context?mapId=${encodeURIComponent(mapId)}`,
				);
				setContext(nextContext);
				setSelectedMapId(mapId);
				setError("");
			} catch {
				setError("The selected Wayfinder spec could not be loaded.");
			}
		},
		[projectId],
	);
	const loadDiscoverySnapshot = useCallback(
		async (mapId: string) => {
			const snapshot = await fetchDiscoverySnapshot(projectId, mapId);
			if (selectedMapIdRef.current !== mapId) return snapshot.revision;
			const latest = latestDiscoveryRevision.current.get(mapId);
			if (!shouldApplyDiscoverySnapshot(latest, snapshot.revision)) return snapshot.revision;
			latestDiscoveryRevision.current.set(mapId, snapshot.revision);
			discoverySnapshotRef.current = snapshot;
			setDiscoverySnapshot(snapshot);
			setMaps((current) =>
				current.some((map) => map.id === snapshot.map.id)
					? current.map((map) => (map.id === snapshot.map.id ? snapshot.map : map))
					: [...current, snapshot.map],
			);
			return snapshot.revision;
		},
		[projectId],
	);
	const requestDiscoveryRefresh = useCallback(
		async (mapId: string) => {
			const coordinator = discoveryRefreshCoordinator.current;
			if (selectedMapIdRef.current === mapId && coordinator) {
				await coordinator.request();
				discoveryLiveConnection.current?.noteAuthoritativeConvergence();
				return;
			}
			await loadDiscoverySnapshot(mapId);
		},
		[loadDiscoverySnapshot],
	);
	const loadMaps = useCallback(async () => {
		const page = await portalFetch<{ items: PortalWorkMapDto[] }>(
			`/api/portal/v1/projects/${projectId}/work-maps?kind=wayfinding&limit=50&offset=0`,
		);
		const nextMaps = page.items.length
			? page.items
			: [
					await portalFetch<PortalWorkMapDto>(`/api/portal/v1/projects/${projectId}/work-maps/wayfinding/ensure`, {
						method: "POST",
						body: JSON.stringify({}),
					}),
				];
		const authoritativeMap = discoverySnapshotRef.current?.map;
		setMaps(
			authoritativeMap && nextMaps.some((map) => map.id === authoritativeMap.id)
				? nextMaps.map((map) => (map.id === authoritativeMap.id ? authoritativeMap : map))
				: nextMaps,
		);
		setSelectedMapId((current) =>
			current && nextMaps.some((map) => map.id === current)
				? current
				: (nextMaps.find((map) => map.status === "active" || map.status === "ready") ?? nextMaps[0])?.id,
		);
		setMapsLoaded(true);
	}, [projectId]);
	useEffect(() => {
		portalFetch<PortalProjectDto>(`/api/portal/v1/projects/${projectId}`).then(setProject);
		loadMaps().catch(() => {
			setMapsLoaded(true);
			setError("The project's Wayfinder maps could not be loaded.");
		});
	}, [loadMaps, projectId]);
	useEffect(() => {
		if (section === "conversation") return;
		loadContext().catch(() => setError("The selected Wayfinder spec could not be loaded."));
	}, [loadContext, section]);
	useEffect(() => {
		discoverySnapshotRef.current = undefined;
		setDiscoverySnapshot(undefined);
		discoveryRefreshCoordinator.current = undefined;
		discoveryLiveConnection.current = undefined;
		if (section !== "conversation" || !selectedMapId) return;
		latestDiscoveryRevision.current.delete(selectedMapId);
		const reportSyncTelemetry = (telemetry: Parameters<typeof reportDiscoverySyncTelemetry>[2]) =>
			reportDiscoverySyncTelemetry(projectId, selectedMapId, telemetry);
		const coordinator = new DiscoveryRefreshCoordinator(
			() => loadDiscoverySnapshot(selectedMapId),
			() => latestDiscoveryRevision.current.get(selectedMapId),
			() => reportSyncTelemetry({ signal: "refreshCoalesced" }),
		);
		const liveConnection = new DiscoveryLiveConnection({
			projectId,
			mapId: selectedMapId,
			url: discoveryEventsUrl(projectId, selectedMapId),
			refresh: (minimumRevision) => coordinator.request(minimumRevision),
			latestAppliedRevision: () => latestDiscoveryRevision.current.get(selectedMapId),
			onTelemetry: reportSyncTelemetry,
			onStateChange: (state) => {
				setDiscoverySyncState(state);
				if (state === "connected") {
					setError((current) => (current.startsWith("The Discovery snapshot could not be") ? "" : current));
				}
			},
		});
		discoveryRefreshCoordinator.current = coordinator;
		discoveryLiveConnection.current = liveConnection;
		coordinator.request().catch(() => {
			setDiscoverySyncState("degraded");
			setError("The Discovery snapshot could not be loaded.");
		});
		liveConnection.start();

		const recover = () => void liveConnection.recover();
		const refreshWhenVisible = () => {
			if (document.visibilityState === "visible") recover();
		};
		document.addEventListener("visibilitychange", refreshWhenVisible);
		window.addEventListener("online", recover);
		return () => {
			liveConnection.stop();
			document.removeEventListener("visibilitychange", refreshWhenVisible);
			window.removeEventListener("online", recover);
			if (discoveryRefreshCoordinator.current === coordinator) discoveryRefreshCoordinator.current = undefined;
			if (discoveryLiveConnection.current === liveConnection) discoveryLiveConnection.current = undefined;
		};
	}, [loadDiscoverySnapshot, projectId, section, selectedMapId]);
	useEffect(() => {
		if (!selectedMapId) return;
		const url = new URL(window.location.href);
		url.searchParams.set("mapId", selectedMapId);
		window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
	}, [selectedMapId]);
	const subnavReady = Boolean(project && mapsLoaded);
	useEffect(() => {
		if (!subnavReady) return;
		const nav = subnavRef.current;
		const item = nav?.querySelector<HTMLElement>(`[data-section="${section}"]`);
		if (!nav || !item) return;
		const centeredLeft = item.offsetLeft - (nav.clientWidth - item.offsetWidth) / 2;
		nav.scrollTo({ left: Math.max(0, centeredLeft), behavior: "auto" });
	}, [subnavReady, section]);
	if (!project || !mapsLoaded)
		return <StatusBanner kind={error ? "error" : "info"}>{error || "Loading the project workspace…"}</StatusBanner>;
	const selectedMap = maps.find((map) => map.id === selectedMapId) ?? maps[0];
	const requirements = section === "conversation" ? discoverySnapshot?.requirements : context?.requirements;
	const readiness = requirements?.readiness;
	const sectionPath = (target: (typeof sections)[number][0]) =>
		`/projects/${projectId}/${target}${selectedMapId ? `?mapId=${encodeURIComponent(selectedMapId)}` : ""}`;
	const sections = [
		["overview", "Overview"],
		["conversation", "Discovery"],
		["spec", "Spec"],
		["delivery", "Delivery"],
		["completion", "Completion"],
		["requests", "Requests"],
		["artifacts", "Artifacts"],
		["inbox", "Inbox"],
		["preview", "Preview"],
	] as const;
	async function ensureLivingSpec() {
		if (!selectedMap) return;
		try {
			const updated = await portalFetch<PortalWorkMapDto>(
				`/api/portal/v1/projects/${projectId}/work-maps/${selectedMap.id}/spec/ensure`,
				{ method: "POST", body: JSON.stringify({}) },
			);
			setMaps((current) => current.map((map) => (map.id === updated.id ? updated : map)));
			await loadContext();
			setError("");
		} catch {
			setError("A living spec could not be created for this Wayfinder map.");
		}
	}
	async function createMap() {
		const title = newMapTitle.trim();
		const destination = newMapDestination.trim();
		if (!title || !destination || creatingMap) return;
		setCreatingMap(true);
		try {
			const created = await portalFetch<PortalWorkMapDto>(`/api/portal/v1/projects/${projectId}/work-maps`, {
				method: "POST",
				body: JSON.stringify({ kind: "wayfinding", title, destination }),
			});
			await loadMaps();
			await selectMap(created.id);
			setNewMapTitle("");
			setNewMapDestination("");
			setShowNewMap(false);
			mobileProjectMenuRef.current?.removeAttribute("open");
			setNotice({ message: `Created the “${created.title}” Wayfinder map.` });
		} catch {
			setError("The new Wayfinder map could not be created. Nothing was changed.");
		} finally {
			setCreatingMap(false);
		}
	}
	return (
		<div className={`project-detail project-detail--${section}`}>
			<header className="route-heading">
				<p className="eyebrow">PROJECT WORKSPACE</p>
				<h1 tabIndex={-1}>{project.name}</h1>
				<div className="project-heading-meta">
					<span className="project-status">{humanizePortalStatus(project.status)}</span>
					<p>{project.description}</p>
					{project.needsFunding && (
						<div className="project-funding-state" role="status">
							<strong>Needs funding</strong>
							<span>{needsFundingReasonLabel(project.needsFunding.reason)}</span>
						</div>
					)}
				</div>
				{maps.length > 0 && selectedMap && (
					<div className="project-map-selector">
						<div className="project-map-control-row">
							<PortalSelect
								className="project-map-picker"
								label="Current Wayfinder map"
								ariaLabel="Choose Wayfinder map"
								value={selectedMap.id}
								options={maps.map((map) => ({
									value: map.id,
									label: map.title,
									description: map.status,
								}))}
								onChange={(mapId) => void selectMap(mapId)}
							/>
							<button
								type="button"
								className="secondary project-new-map-trigger"
								onClick={() => setShowNewMap(true)}
							>
								New map
							</button>
						</div>
						{showNewMap && (
							<form
								className="project-new-map-popover"
								onSubmit={(event) => {
									event.preventDefault();
									void createMap();
								}}
							>
								<header>
									<div>
										<strong>Start another destination</strong>
										<small>Its questions and decisions stay separate.</small>
									</div>
									<button type="button" className="text-button" onClick={() => setShowNewMap(false)}>
										Cancel
									</button>
								</header>
								<label>
									<span>Map name</span>
									<input
										value={newMapTitle}
										onChange={(event) => setNewMapTitle(event.target.value)}
										placeholder="For example, Mobile launch"
										maxLength={255}
										required
									/>
								</label>
								<label>
									<span>Destination</span>
									<textarea
										value={newMapDestination}
										onChange={(event) => setNewMapDestination(event.target.value)}
										placeholder="What decision-complete outcome should this map reach?"
										rows={3}
										required
									/>
								</label>
								<button
									type="submit"
									disabled={creatingMap || !newMapTitle.trim() || !newMapDestination.trim()}
								>
									{creatingMap ? "Creating…" : "Create map"}
								</button>
							</form>
						)}
					</div>
				)}
			</header>
			<nav ref={subnavRef} className="project-subnav" aria-label={`${project.name} sections`}>
				{sections.map(([item, label]) => (
					<button
						type="button"
						key={item}
						data-section={item}
						className={section === item ? "active" : undefined}
						aria-current={section === item ? "page" : undefined}
						onClick={() => navigatePortal(sectionPath(item))}
					>
						{label}
					</button>
				))}
			</nav>
			<div className={`project-section-content project-section-content--${section}`}>
				{error && <StatusBanner kind="error">{error}</StatusBanner>}
				{notice && <StatusBanner kind="info">{notice.message}</StatusBanner>}
				{section === "overview" && selectedMap && (
					<div className="project-overview">
						<section className="project-wayfinder-summary">
							<p className="eyebrow">SELECTED WORK SLICE</p>
							<h2>{selectedMap.title}</h2>
							<p>{selectedMap.destination}</p>
							<p className="visibility-note">
								This map may describe a whole small app or one feature inside a larger product. Its questions,
								conversation, and living spec stay together.
							</p>
						</section>
						{readiness && <SpecProgress readiness={readiness} />}
						<section className="project-direction">
							<p className="eyebrow">LIVING SPEC</p>
							<h2>{requirements?.specTitle ?? "No living spec linked yet"}</h2>
							{requirements ? (
								<>
									<div className="project-direction-copy">
										<MessageBody content={requirements.content} markdown />
									</div>
									<div className="action-row">
										<button type="button" onClick={() => navigatePortal(sectionPath("conversation"))}>
											{readiness?.stage === "discovery" ? "Continue discovery" : "Open conversation"}
										</button>
										<button
											type="button"
											className="secondary"
											onClick={() => navigatePortal(sectionPath("spec"))}
										>
											View this living spec
										</button>
									</div>
								</>
							) : (
								<div className="compact-empty-state">
									<p>This older map is not linked to a spec. Create one without changing any other map.</p>
									<button type="button" onClick={() => void ensureLivingSpec()}>
										Start its living spec
									</button>
								</div>
							)}
						</section>
					</div>
				)}
				{section !== "overview" && !requirements && (
					<StatusBanner kind="info">Choose a map with a living spec, or start one from Overview.</StatusBanner>
				)}
				{section === "conversation" && requirements && selectedMap && (
					<DiscoveryWorkspace
						initialMessage={correctionMessage?.mapId === selectedMap.id ? correctionMessage : undefined}
						onInitialMessageSent={() => setCorrectionMessage(undefined)}
						projectId={projectId}
						maps={maps}
						selectedMapId={selectedMap.id}
						activePane={activeDiscoveryPane}
						onActivePaneChange={setActiveDiscoveryPane}
						onMapsUpdated={() => requestDiscoveryRefresh(selectedMap.id)}
						voiceAvailable={voiceAvailable}
						agentModel={agentModel}
						readiness={discoverySnapshot?.requirements?.readiness ?? requirements.readiness}
						discoverySnapshot={discoverySnapshot}
						syncState={discoverySyncState}
						mobileProjectMenu={
							<details ref={mobileProjectMenuRef} className="mobile-project-menu">
								<summary role="button" aria-label={`Open project menu for ${project.name}`}>
									<span>
										<strong>{project.name}</strong>
										<small>
											{project.needsFunding
												? `Needs funding — ${needsFundingReasonLabel(project.needsFunding.reason)}`
												: selectedMap.title}
										</small>
									</span>
								</summary>
								<div className="mobile-project-menu-panel">
									<header>
										<span>Project workspace</span>
										<strong>{project.name}</strong>
										<small>{humanizePortalStatus(project.status)}</small>
										{project.needsFunding && (
											<p className="project-funding-state">
												<strong>Needs funding</strong>
												<span>{needsFundingReasonLabel(project.needsFunding.reason)}</span>
											</p>
										)}
									</header>
									<nav aria-label="Portal destinations">
										<button
											type="button"
											onClick={() => {
												mobileProjectMenuRef.current?.removeAttribute("open");
												navigatePortal("/projects");
											}}
										>
											All projects
										</button>
										<button
											type="button"
											onClick={() => {
												mobileProjectMenuRef.current?.removeAttribute("open");
												navigatePortal("/inbox/all");
											}}
										>
											Portal inbox
										</button>
									</nav>
									<section aria-labelledby="mobile-project-sections">
										<span id="mobile-project-sections">Project sections</span>
										<div className="mobile-project-menu-grid">
											{sections.map(([item, label]) => (
												<button
													type="button"
													key={item}
													className={section === item ? "active" : undefined}
													aria-current={section === item ? "page" : undefined}
													onClick={() => {
														mobileProjectMenuRef.current?.removeAttribute("open");
														navigatePortal(sectionPath(item));
													}}
												>
													{label}
												</button>
											))}
										</div>
									</section>
									<section aria-labelledby="mobile-wayfinder-maps">
										<span id="mobile-wayfinder-maps">Wayfinder maps</span>
										<div className="mobile-project-map-list">
											{maps.map((map) => (
												<button
													type="button"
													key={map.id}
													aria-pressed={map.id === selectedMap.id}
													onClick={() => {
														mobileProjectMenuRef.current?.removeAttribute("open");
														void selectMap(map.id);
													}}
												>
													<span>{map.title}</span>
													<small>{map.status}</small>
												</button>
											))}
										</div>
										<button
											type="button"
											className="secondary mobile-new-map-trigger"
											onClick={() => {
												setShowNewMap(true);
												mobileProjectMenuRef.current?.removeAttribute("open");
												navigatePortal(sectionPath("overview"));
											}}
										>
											Start a new map
										</button>
									</section>
								</div>
							</details>
						}
					/>
				)}{" "}
				{section === "spec" && requirements && (
					<SpecView
						specId={requirements.specId}
						onCorrectionsStarted={async ({ mapId, context: nextContext }) => {
							if (selectedMapIdRef.current !== selectedMapId) return;
							const snapshot = await fetchDiscoverySnapshot(projectId, mapId);
							if (selectedMapIdRef.current !== selectedMapId) return;
							setMaps((current) => [...current.filter((map) => map.id !== mapId), snapshot.map]);
							selectedMapIdRef.current = mapId;
							setSelectedMapId(mapId);
							discoverySnapshotRef.current = snapshot;
							setDiscoverySnapshot(snapshot);
							setContext(nextContext);
							setCorrectionMessage({
								key: crypto.randomUUID(),
								mapId,
								message:
									"Help me work through the failed validation and specify only the changes and regression checks needed on top of the accepted baseline.",
							});
							setActiveDiscoveryPane("chat");
							navigatePortal(`/projects/${projectId}/conversation?mapId=${encodeURIComponent(mapId)}`);
						}}
						projectId={projectId}
						mapId={selectedMapId as string}
						readiness={requirements.readiness}
						requirementsContent={requirements.content}
						acceptanceTracker={context?.acceptanceTracker ?? null}
						onAcceptanceTrackerUpdated={(acceptanceTracker) =>
							setContext((current) => (current ? { ...current, acceptanceTracker } : current))
						}
						onContinueDiscovery={() => {
							setActiveDiscoveryPane("chat");
							navigatePortal(sectionPath("conversation"));
						}}
					/>
				)}{" "}
				{section === "delivery" && requirements && (
					<ProjectDelivery
						projectId={projectId}
						mapId={selectedMapId as string}
						specId={requirements.specId}
						readiness={requirements.readiness}
						acceptanceTracker={context?.acceptanceTracker ?? null}
						onOpenSpec={() => navigatePortal(sectionPath("spec"))}
						onContinueDiscovery={() =>
							navigatePortal(
								requirements.readiness.stage === "adminReview"
									? sectionPath("spec")
									: sectionPath("conversation"),
							)
						}
					/>
				)}{" "}
				{section === "completion" && requirements && (
					<ProjectCompletion
						projectId={projectId}
						specId={requirements.specId}
						readiness={requirements.readiness}
						acceptanceTracker={context?.acceptanceTracker ?? null}
					/>
				)}{" "}
				{section === "requests" && <ProjectRequests projectId={projectId} />}{" "}
				{section === "artifacts" && selectedMap && (
					<ArtifactView key={selectedMap.id} projectId={projectId} projectName={project.name} map={selectedMap} />
				)}{" "}
				{section === "inbox" && <InboxView projectId={projectId} pushPublicKey={pushPublicKey} />}{" "}
				{section === "preview" && requirements && (
					<PreviewView
						projectId={projectId}
						available={previewAvailable}
						readyForDelivery={requirements.readiness.readyForExecution}
						waitingForAdmin={requirements.readiness.stage === "adminReview"}
					/>
				)}
			</div>
		</div>
	);
}
